import React, { useState } from "react";
import axios from "axios";
import { message } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import style from "css/signup/signup.module.css";
import SEOMeta from "component/app/SEOMeta";
import TermsModal from "component/common/termsModal";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function SignUp() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeService, setAgreeService] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  //열려있는 약관 전문 종류 ("service" | "collect" | "privacy" | null)
  const [termsType, setTermsType] = useState(null);

  const hasInput = email.length > 0;
  const isEmailValid = EMAIL_REGEX.test(email.trim());
  const agreeAll = agreePrivacy && agreeService;
  const canSubmit = isEmailValid && agreeAll && !submitting;
  const showFormatError = agreeAll && hasInput && !isEmailValid;
  const showError = showFormatError || apiError !== null;

  const toggleAll = () => {
    const next = !agreeAll;
    setAgreePrivacy(next);
    setAgreeService(next);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    const to = email.trim();
    setApiError(null);
    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/auth/sendSignupMail`,
        { to },
      );
      navigate("/signup/verify", { state: { email: to } });
    } catch (err) {
      const data = err?.response?.data;
      const errCode =
        typeof data === "string" ? data : data?.userMsg || data?.UserMsg;
      if (errCode === "DUPLICATED_EMAIL") {
        setApiError("DUPLICATED_EMAIL");
      } else {
        message.error(t("signup.email.sendFail"));
      }
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div className={style.page}>
      <SEOMeta
        title={t("signup.email.seo.title")}
        description={t("signup.email.seo.description")}
        keywords={t("signup.email.seo.keywords")}
      />
      <Link
        to="/"
        className={style.logoLink}
      >
        <img
          alt="all Place"
          src={`${process.env.REACT_APP_CLIENT_URL}img/common/allplace_logo_dark.png`}
        />
      </Link>
      <div className={style.card}>
        <h1 className={style.title}>
          <Trans
            i18nKey="signup.email.title"
            components={{ br: <br className={style.mobileBr} /> }}
          />
        </h1>
        <p className={style.subtitle}>{t("signup.email.subtitle")}</p>
        <input
          className={`${style.emailInput} ${
            showError ? style.emailInputError : ""
          }`}
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={t("signup.email.emailPlaceholder")}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (apiError) setApiError(null);
          }}
          onKeyDown={handleKeyDown}
        />
        {showError && (
          <div className={style.errorBlock}>
            <img
              className={style.errorIcon}
              src={`${process.env.REACT_APP_CLIENT_URL}img/common/warm-circle.svg`}
              alt=""
            />
            <div className={style.errorMessages}>
              {apiError === "DUPLICATED_EMAIL" ? (
                <>
                  <p className={style.errorPrimary}>
                    {t("signup.email.errorDuplicatedPrimary")}
                  </p>
                  <p className={style.errorSecondary}>
                    {t("signup.email.errorDuplicatedSecondary")}
                  </p>
                </>
              ) : (
                <>
                  <p className={style.errorPrimary}>
                    {t("signup.email.errorFormatPrimary")}
                  </p>
                  <p className={style.errorSecondary}>
                    {t("signup.email.errorFormatSecondary")}
                  </p>
                </>
              )}
            </div>
          </div>
        )}
        {hasInput && (
          <div className={style.termsSection}>
            <button
              type="button"
              className={style.termsAllRow}
              onClick={toggleAll}
            >
              <span
                className={`${style.termsCheckbox} ${
                  agreeAll ? style.termsCheckboxChecked : ""
                }`}
              />
              <span className={style.termsAllLabel}>
                {t("signup.email.agreeAll")}
              </span>
            </button>
            <div className={style.termsDivider} />
            <div className={style.termsItemRow}>
              <button
                type="button"
                className={style.termsToggle}
                role="checkbox"
                aria-checked={agreeService}
                onClick={() => setAgreeService((v) => !v)}
              >
                <span
                  className={`${style.termsCheckbox} ${
                    agreeService ? style.termsCheckboxChecked : ""
                  }`}
                />
                <span className={style.termsRequired}>
                  {t("signup.email.required")}
                </span>
              </button>
              <button
                type="button"
                className={style.termsLink}
                onClick={() => setTermsType("service")}
                aria-label={t("terms.viewDetail", {
                  name: t("signup.email.termsService"),
                })}
              >
                {t("signup.email.termsService")}
              </button>
            </div>
            <div className={style.termsItemRow}>
              <button
                type="button"
                className={style.termsToggle}
                role="checkbox"
                aria-checked={agreePrivacy}
                onClick={() => setAgreePrivacy((v) => !v)}
              >
                <span
                  className={`${style.termsCheckbox} ${
                    agreePrivacy ? style.termsCheckboxChecked : ""
                  }`}
                />
                <span className={style.termsRequired}>
                  {t("signup.email.required")}
                </span>
              </button>
              <button
                type="button"
                className={style.termsLink}
                onClick={() => setTermsType("collect")}
                aria-label={t("terms.viewDetail", {
                  name: t("signup.email.termsPrivacy"),
                })}
              >
                {t("signup.email.termsPrivacy")}
              </button>
            </div>
            {/* 개인정보처리방침은 공개 문서이므로 동의 대상에서 제외하고 링크로만 노출 */}
            <div className={style.termsPolicyRow}>
              <button
                type="button"
                className={style.termsPolicyLink}
                onClick={() => setTermsType("privacy")}
              >
                {t("signup.email.privacyPolicy")}
              </button>
            </div>
          </div>
        )}
        <button
          type="button"
          className={`${style.submitBtn} ${
            canSubmit ? style.submitBtnActive : style.submitBtnDisabled
          }`}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          {t("signup.email.next")}
        </button>
      </div>
      <TermsModal
        type={termsType}
        onClose={() => setTermsType(null)}
        onAgree={(docType) => {
          if (docType === "collect") setAgreePrivacy(true);
          if (docType === "service") setAgreeService(true);
        }}
      />
    </div>
  );
}

export default SignUp;
